import React from 'react';
import {StyleSheet, TouchableHighlight, View} from 'react-native';
import Animated, {FadeIn, FadeOut, Layout} from 'react-native-reanimated';

import {AirtableFeedItem, AirtablePromotionItem} from '../../models/airtable';
import {isDrawnWinnersPromotion} from '../../stores/promotionsStore';
import theme from '../../style/theme';
import {getFeedDiscoverTileImage} from '../../utils/feedAndDiscover/media';
import {getHeroImageDetail} from '../../utils/heroImage';
import {getFirstTwoLines} from '../../utils/string';
import Markdown from '../ui-controls/Markdown';
import Text from '../ui-controls/Text';

import {AirtableContentItem} from './models';

type BaseTileProps = {
  item: AirtableContentItem;
  customStyles: any;
  pressCallback: (feedItem: AirtableContentItem) => void;
  paused: boolean;
  visibleCallback?: (isVisible: boolean) => void;
  includeByline: boolean;
  isWebDesktopScreen?: boolean;
  headlineVariant?: 'heading1' | 'heading2' | 'heading3' | 'heading4';
};

const BaseTile = ({
  item,
  customStyles,
  pressCallback = () => {},
  paused = true,
  visibleCallback = () => {},
  includeByline = false,
  isWebDesktopScreen = false,
  headlineVariant = 'heading3',
}: BaseTileProps) => {
  // // Used only in tiles on the Feed screen. Discover has too many
  // // instances of videos playing that wrapping them in InViewPort
  // // shits the UI thread on iOS. Easiest to just let them keep on
  // // playing and not bother pausing them when they're out of view.
  // const [paused, setPaused] = useState(true);
  // useFocusEffect(
  //   React.useCallback(() => {
  //     // Do something when the screen is focused
  //     // component.current.startAutoplay(false);
  //     setPaused(false);
  //
  //     return () => {
  //       // Do something when the screen is unfocused
  //       // Useful for cleanup functions
  //       // component.current.stopAutoplay();
  //       setPaused(true);
  //     };
  //   }, []),
  // );

  const itemAsPromotion = item as AirtablePromotionItem;
  const itemAsFeedItem = item as AirtableFeedItem;

  const isDrawnWinners = isDrawnWinnersPromotion(itemAsPromotion);
  const imageDetail = getHeroImageDetail(item, isWebDesktopScreen);

  return (
    <Animated.View
      entering={FadeIn.duration(500)}
      exiting={FadeOut.duration(250)}
      layout={Layout.easing()}>
      <TouchableHighlight
        onPress={() => pressCallback(item)}
        underlayColor={theme.colour.general.secondary}
        style={[styles.touchable, customStyles.touchable]}
        activeOpacity={0.7}
        delayPressIn={150}>
        <View
          style={[styles.container, customStyles.container]}
          testID="base-tile-container">
          {getFeedDiscoverTileImage(imageDetail, paused, visibleCallback)}
          <View
            style={[styles.textHolder, customStyles.textHolder]}
            testID="base-tile-text-holder">
            <Text
              variant={headlineVariant}
              style={customStyles.headline}
              testID="base-tile-headline">
              {itemAsFeedItem.tileHeadline ||
                itemAsPromotion.bannerHeadline ||
                item.headline}
            </Text>
            {isDrawnWinners && itemAsPromotion.drawDate ? (
              <Text
                variant="caption"
                testID="base-tile-description"
                style={styles.drawnWinnersCaptionCopy}
                adjustsFontSizeToFit={false}>
                Draw date: {itemAsPromotion.drawDate}
              </Text>
            ) : (
              (itemAsFeedItem.firstCopy || itemAsPromotion.description) &&
              !!includeByline && (
                <Markdown variant="caption">
                  {getFirstTwoLines(
                    itemAsFeedItem.firstCopy || itemAsPromotion.description,
                  )}
                </Markdown>
              )
            )}
          </View>
        </View>
      </TouchableHighlight>
    </Animated.View>
  );
};

export default BaseTile;

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: '#CCC',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
    marginHorizontal: 0,
  },
  touchable: {
    borderRadius: 4,
    marginBottom: 8,
  },
  textHolder: {
    width: '100%',
    backgroundColor: 'white',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  headline: {
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
  },
  drawnWinnersCaptionCopy: {
    paddingTop: 2,
  },
});
